import { useCallback, useRef } from 'react';
import { Button } from 'flowbite-react';
import { isAxiosError } from 'axios';
import { enqueueSnackbar } from 'notistack';
import dayjs from 'dayjs';
import { Dispute } from 'api/escrow/types';
import axiosClient from 'api/config/axios';
import { useDataValues } from 'providers/DataValues';
import { useFundArbitrationFeeMutation } from 'api/mutations/escrow/fund-arbitration-fee';
import { useFundInspectionCostMutation } from 'api/mutations/escrow/fund-inspection-cost';
import {
  DEFAULT_PAYMENT_PROVIDERS,
  getPaymentProvidersQueryKey,
  useGetPaymentProvidersQuery,
} from 'api/queries/escrow/get-payment-providers';
import { useModals } from 'providers/Modals';
import { memoryQueryClient } from 'providers/TanstackQuery';
import { getDisputeStatusQueryKey } from 'api/queries/escrow/get-dispute-status';
import { flattenProviderOptions } from 'utils/flatten-provider-options';

type Props = { dispute: Dispute };

/** Marker discriminating which dispute funding flow is in progress at any moment. */
type FundingKind = 'arbitration-fee' | 'inspection-cost';

export default function ArbitrationFundingSection({ dispute }: Props) {
  const { userId } = useDataValues();
  const { mutateAsync: fundArbitrationFee, isPending: isFundingFee } = useFundArbitrationFeeMutation();
  const { mutateAsync: fundInspectionCost, isPending: isFundingInspection } = useFundInspectionCostMutation();
  const { data: providerMap } = useGetPaymentProvidersQuery();
  const { open: openModal } = useModals();

  const isBuyer = dispute.tradeEscrow?.buyerId === +userId;
  const myFeeFunded = isBuyer ? dispute.arbitrationFeeBuyerFunded : dispute.arbitrationFeeSellerFunded;
  const counterpartyFeeFunded = isBuyer ? dispute.arbitrationFeeSellerFunded : dispute.arbitrationFeeBuyerFunded;
  const myInspectionFunded = isBuyer ? dispute.inspectionCostBuyerFunded : dispute.inspectionCostSellerFunded;
  const counterpartyInspectionFunded = isBuyer ? dispute.inspectionCostSellerFunded : dispute.inspectionCostBuyerFunded;

  // derive the trade currency from the escrow or fall back to the first payment order
  const currency = dispute.tradeEscrow?.currency ?? dispute.tradeEscrow?.paymentOrders?.[0]?.currency ?? '';

  /**
   * Forward reference to the funding orchestrator so the inline error
   * branches in `runFunding` can re-call the picker after invalidating the
   * providers cache. The orchestrator captures the funding kind plus the
   * `retryAttempted` flag, so the retry pass is told apart from a fresh
   * user click.
   */
  const startFundingFlowRef = useRef<((kind: FundingKind, retryAttempted: boolean) => void) | null>(null);

  /**
   * Opens the combined payment-method picker and invokes `onPicked` once the
   * user has chosen a payment option. Flattens the provider/method matrix
   * into a single list of options (e.g. "PayPal", "GCash", "QRPh") so the
   * user picks what they actually recognise without first choosing a PSP.
   *
   * Decision tree:
   * - 0 options for the currency → error snackbar
   * - 1 option → auto-select without showing the picker
   * - 2+ options → open the combined picker
   */
  const pickProviderAndMethod = useCallback(
    (onPicked: (driver: string, paymentMethod?: string) => void) => {
      const map = providerMap ?? DEFAULT_PAYMENT_PROVIDERS;
      const providers = map[currency] ?? [];

      const paymentOptions = flattenProviderOptions(providers);

      if (paymentOptions.length === 0) {
        enqueueSnackbar(`No payment providers are available for ${currency}. Please contact support.`, {
          variant: 'error',
        });
        return;
      }

      if (paymentOptions.length === 1) {
        const only = paymentOptions[0];
        onPicked(only.driver, only.paymentMethod);
        return;
      }

      openModal('method-selection', {
        currency,
        options: paymentOptions,
        onOptionSelected: (driver: string, paymentMethod?: string) => {
          onPicked(driver, paymentMethod);
        },
      });
    },
    [currency, openModal, providerMap],
  );

  /**
   * Fetches the redirect URL from the funding-mutation's returned `checkoutUrl`
   * and navigates the browser there. The funding endpoints already create the
   * payment with the chosen driver+method, so this hop is purely the
   * Xendit/PayPal hosted-page redirect — no second `initiatePayment` call is
   * issued.
   */
  const redirectViaCheckoutUrl = useCallback(
    async (checkoutUrl: string | null, onStaleMethodRetry: () => void, retryAttempted: boolean) => {
      if (!checkoutUrl) {
        enqueueSnackbar('Payment checkout URL not available, please retry.', { variant: 'error' });
        return;
      }
      try {
        const checkoutResponse = await axiosClient.query.get(checkoutUrl, {
          headers: { Accept: 'application/json' },
        });
        const redirectUrl = checkoutResponse.data?.redirect_url;
        // payment gateways always serve over HTTPS — reject plain HTTP redirects to avoid
        // leaking checkout tokens / sensitive query parameters in transit
        if (typeof redirectUrl === 'string' && redirectUrl.startsWith('https://')) {
          window.location.href = redirectUrl;
        } else {
          enqueueSnackbar('Invalid payment URL received. Please try again.', { variant: 'error' });
        }
      } catch (err: unknown) {
        if (isAxiosError(err) && err.response?.status === 409) {
          // stale-method 409 — anti-loop guarded auto-retry
          const message =
            (err.response.data as { message?: string })?.message || 'Payment method is no longer available.';
          enqueueSnackbar(message, { variant: 'error' });
          if (!retryAttempted) {
            void memoryQueryClient.invalidateQueries({ queryKey: getPaymentProvidersQueryKey() });
            onStaleMethodRetry();
          } else {
            enqueueSnackbar('Payment is currently unavailable. Please try again later.', { variant: 'error' });
          }
        } else if (isAxiosError(err) && err.response?.status === 404) {
          enqueueSnackbar('Payment checkout URL not available, please retry.', { variant: 'error' });
        } else {
          enqueueSnackbar('Failed to open payment page. Please try again.', { variant: 'error' });
        }
      }
    },
    [],
  );

  /**
   * Runs a single funding attempt for the chosen kind with the given driver/method,
   * handling 409/422 from the funding mutation under the same anti-loop semantics
   * as the trade pay flow.
   */
  const runFunding = useCallback(
    async (kind: FundingKind, driver: string, paymentMethod: string | undefined, retryAttempted: boolean) => {
      const mutate = kind === 'arbitration-fee' ? fundArbitrationFee : fundInspectionCost;

      try {
        const result = await mutate({ disputeId: dispute.id, driver, paymentMethod });
        if (!result.payment) {
          enqueueSnackbar(
            kind === 'arbitration-fee'
              ? 'Invoice is being prepared. Please check back shortly.'
              : 'No inspection cost required or invoice is being prepared.',
            { variant: 'info' },
          );
          return;
        }

        await redirectViaCheckoutUrl(
          result.payment.checkoutUrl,
          () => startFundingFlowRef.current?.(kind, true),
          retryAttempted,
        );
      } catch (err: unknown) {
        if (isAxiosError(err) && err.response?.status === 409) {
          const message = (err.response.data as { message?: string })?.message || 'Payment method unavailable.';
          enqueueSnackbar(message, { variant: 'error' });
        } else if (isAxiosError(err) && err.response?.status === 422) {
          const message =
            (err.response.data as { message?: string })?.message || 'Selected payment method is unavailable.';
          enqueueSnackbar(message, { variant: 'error' });
          if (!retryAttempted) {
            void memoryQueryClient.invalidateQueries({ queryKey: getPaymentProvidersQueryKey() });
            startFundingFlowRef.current?.(kind, true);
          } else {
            enqueueSnackbar('Payment is currently unavailable. Please try again later.', { variant: 'error' });
          }
        } else {
          enqueueSnackbar('Failed to open payment page. Please try again.', { variant: 'error' });
        }
      } finally {
        void memoryQueryClient.refetchQueries({ queryKey: getDisputeStatusQueryKey(dispute.id) });
      }
    },
    [dispute.id, fundArbitrationFee, fundInspectionCost, redirectViaCheckoutUrl],
  );

  /**
   * Top-level entry point for either funding flow: opens the provider/method
   * picker, then dispatches `runFunding` with the chosen values. The
   * `retryAttempted` flag is forwarded so the anti-loop branches inside
   * `runFunding` and `redirectViaCheckoutUrl` know whether they're on the
   * second attempt and should fall through to the generic snackbar instead
   * of re-opening the picker again.
   */
  const startFundingFlow = useCallback(
    (kind: FundingKind, retryAttempted: boolean) => {
      pickProviderAndMethod((driver, paymentMethod) => {
        void runFunding(kind, driver, paymentMethod, retryAttempted);
      });
    },
    [pickProviderAndMethod, runFunding],
  );

  // wire the forward reference so runFunding's anti-loop branches can re-enter the picker
  startFundingFlowRef.current = startFundingFlow;

  const handleFundFee = () => startFundingFlow('arbitration-fee', false);
  const handleFundInspection = () => startFundingFlow('inspection-cost', false);

  return (
    <div className="bg-brand-purple-card rounded-lg p-4 md:p-6">
      <h3 className="text-lg font-bold text-brand-black mb-4">Arbitration Funding</h3>

      {dispute.fundingDeadline && (
        <p className="text-sm text-brand-red-main font-medium mb-4">
          Funding deadline: {dayjs(dispute.fundingDeadline).format('MMM D, YYYY h:mm A')}
        </p>
      )}

      <div className="mb-4">
        <p className="text-sm font-semibold text-brand-black mb-2">Arbitration Fee</p>
        <div className="flex flex-col gap-2 mb-2">
          <div className="flex items-center gap-2">
            <span className={`w-2 h-2 rounded-full ${myFeeFunded ? 'bg-green-500' : 'bg-gray-300'}`} />
            <span className="text-sm text-brand-black">You: {myFeeFunded ? 'Funded' : 'Not funded'}</span>
          </div>
          <div className="flex items-center gap-2">
            <span className={`w-2 h-2 rounded-full ${counterpartyFeeFunded ? 'bg-green-500' : 'bg-gray-300'}`} />
            <span className="text-sm text-brand-black">
              Other party: {counterpartyFeeFunded ? 'Funded' : 'Not funded'}
            </span>
          </div>
        </div>
        {!myFeeFunded && (
          <Button
            pill
            color="failure"
            className="bg-brand-red-main font-semibold"
            onClick={handleFundFee}
            isProcessing={isFundingFee}
            disabled={isFundingFee}
          >
            Fund Arbitration Fee
          </Button>
        )}
      </div>

      <div>
        <p className="text-sm font-semibold text-brand-black mb-2">Inspection Cost</p>
        <div className="flex flex-col gap-2 mb-2">
          <div className="flex items-center gap-2">
            <span className={`w-2 h-2 rounded-full ${myInspectionFunded ? 'bg-green-500' : 'bg-gray-300'}`} />
            <span className="text-sm text-brand-black">You: {myInspectionFunded ? 'Funded' : 'Not funded'}</span>
          </div>
          <div className="flex items-center gap-2">
            <span className={`w-2 h-2 rounded-full ${counterpartyInspectionFunded ? 'bg-green-500' : 'bg-gray-300'}`} />
            <span className="text-sm text-brand-black">
              Other party: {counterpartyInspectionFunded ? 'Funded' : 'Not funded'}
            </span>
          </div>
        </div>
        {!myInspectionFunded && (
          <Button
            pill
            color="failure"
            className="bg-brand-red-main font-semibold"
            onClick={handleFundInspection}
            isProcessing={isFundingInspection}
            disabled={isFundingInspection}
          >
            Fund Inspection Cost
          </Button>
        )}
      </div>
    </div>
  );
}
